import { User, type UserProfile } from 'oidc-client-ts'
import { ABP_AUTH_TOKEN_STORAGE, ABP_TENANT_COOKIE, ABP_USER_ID_STORAGE, BUXUS_API_BASE_URL } from '../../platform/abp/config'
import {
  AUTH_STORAGE_PURGED_KEY,
  callAccountLogout,
  purgeClientAuthStorage,
} from '../../platform/auth/authSessionTeardown'
import { setCookie } from '../../platform/abp/cookies'
import { oidcManager } from '../../platform/auth/oidcManager'

export type ScenarioRegistrationResult = {
  sessionId: string
  email: string
  signedIn: boolean
  tenantId: string | null
}

type InvitePreview = {
  email: string
  sessionId: string
}

function decodeBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='))
  return json
}

/** Display fields from the signed link. The server checks the signature on submit. */
export function readScenarioInviteToken(token: string): InvitePreview | null {
  const body = token.trim().split('.')[0]
  if (!body) return null
  try {
    const parsed = JSON.parse(decodeBase64Url(body)) as Record<string, unknown>
    const email = typeof parsed.email === 'string' ? parsed.email.trim() : ''
    const sessionId = typeof parsed.sessionId === 'string' ? parsed.sessionId.trim() : ''
    const exp = typeof parsed.exp === 'number' ? parsed.exp : Number(parsed.exp)
    if (!email || !sessionId) return null
    if (Number.isFinite(exp) && exp * 1000 < Date.now()) return null
    return { email, sessionId }
  } catch {
    return null
  }
}

function readErrorMessage(body: string, fallback: string): string {
  try {
    const parsed = JSON.parse(body) as {
      error?: { message?: string; details?: string } | string
      error_description?: string
      message?: string
    }
    if (typeof parsed.error_description === 'string' && parsed.error_description.trim()) {
      return parsed.error_description.trim()
    }
    if (parsed.error && typeof parsed.error === 'object') {
      const message = parsed.error.message?.trim()
      const details = parsed.error.details?.trim()
      if (message) return message
      if (details) return details
    }
    if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message.trim()
  } catch {
    if (body.trim()) return body.trim()
  }
  return fallback
}

export async function registerScenarioAccount(input: {
  token: string
  firstName: string
  lastName: string
  password: string
  state: string
  county: string
  organizationName: string
}): Promise<ScenarioRegistrationResult> {
  const res = await fetch(`${BUXUS_API_BASE_URL}/scenarios/register`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: input.token,
      firstName: input.firstName,
      lastName: input.lastName,
      password: input.password,
      state: input.state,
      county: input.county,
      organizationName: input.organizationName,
    }),
  })
  const text = await res.text()
  if (!res.ok) {
    throw new Error(readErrorMessage(text, 'Your registration could not be completed. Please try again.'))
  }
  const parsed = JSON.parse(text) as Record<string, unknown>
  const row =
    parsed.result && typeof parsed.result === 'object'
      ? (parsed.result as Record<string, unknown>)
      : parsed
  const sessionId = typeof row.sessionId === 'string' ? row.sessionId : ''
  const email = typeof row.email === 'string' ? row.email : ''
  if (!sessionId) {
    throw new Error('Your registration could not be completed. Please try again.')
  }
  return {
    sessionId,
    email,
    signedIn: row.signedIn === true,
    tenantId: typeof row.tenantId === 'string' && row.tenantId.trim() ? row.tenantId.trim() : null,
  }
}

/** Someone who already has an account: stamp this session and do not show the create form. */
export async function claimExistingScenarioAccount(token: string): Promise<{
  accountExists: boolean
  sessionId: string
  email: string
}> {
  const res = await fetch(`${BUXUS_API_BASE_URL}/scenarios/register/existing`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  const text = await res.text()
  if (!res.ok) {
    throw new Error(readErrorMessage(text, 'This invitation link is not valid or has expired.'))
  }
  const parsed = JSON.parse(text) as Record<string, unknown>
  const row =
    parsed.result && typeof parsed.result === 'object'
      ? (parsed.result as Record<string, unknown>)
      : parsed
  const sessionId = typeof row.sessionId === 'string' ? row.sessionId : ''
  const email = typeof row.email === 'string' ? row.email : ''
  return {
    accountExists: row.accountExists === true,
    sessionId,
    email,
  }
}

function decodeJwt(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const parsed = JSON.parse(decodeBase64Url(part)) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Email of the account already stored in this browser, if one is signed in. */
export async function signedInEmail(): Promise<string> {
  const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim()
  const userId = localStorage.getItem(ABP_USER_ID_STORAGE)?.trim()
  if (!token || !userId) return ''
  const oidcUser = await oidcManager.getUser().catch(() => null)
  const profile = oidcUser?.profile as Record<string, unknown> | undefined
  const fromProfile = typeof profile?.email === 'string' ? profile.email.trim() : ''
  if (fromProfile) return fromProfile
  const claims = decodeJwt(token)
  return typeof claims?.email === 'string' ? claims.email.trim() : ''
}

/** Password grant for the tenant the new account was created in. Replaces any account already signed in here. */
export async function signInForScenarioTenant(
  email: string,
  password: string,
  tenantId: string,
): Promise<void> {
  setCookie(ABP_TENANT_COOKIE, tenantId)
  const authority = oidcManager.settings.authority.replace(/\/$/, '')
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: oidcManager.settings.client_id,
    username: email,
    password,
    scope: oidcManager.settings.scope ?? 'openid profile email offline_access Buxus',
    // The host's only tenant resolver ignores the __tenant header. The password
    // grant reads this form field and looks the new user up in that tenant.
    TenantId: tenantId,
  })
  const res = await fetch(`${authority}/connect/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      __tenant: tenantId,
      'Abp.TenantId': tenantId,
    },
    body,
  })
  const text = await res.text()
  if (!res.ok) {
    throw new Error(readErrorMessage(text, 'Your account was created. Sign in with the password you just chose.'))
  }
  const token = JSON.parse(text) as {
    access_token?: string
    refresh_token?: string
    expires_in?: number
  }
  if (!token.access_token) {
    throw new Error('Your account was created. Sign in with the password you just chose.')
  }
  const previousToken = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)
  if (previousToken && previousToken !== token.access_token) {
    await callAccountLogout(previousToken)
  }
  await purgeClientAuthStorage()
  const claims = decodeJwt(token.access_token) ?? {}
  const sub = typeof claims.sub === 'string' && claims.sub ? claims.sub : email
  const profile = { ...claims, sub, email } as UserProfile
  const user = new User({
    access_token: token.access_token,
    refresh_token: token.refresh_token,
    token_type: 'Bearer',
    scope: oidcManager.settings.scope,
    session_state: null,
    profile,
    expires_at: Math.floor(Date.now() / 1000) + (token.expires_in && token.expires_in > 0 ? token.expires_in : 3600),
  })
  await oidcManager.storeUser(user)
  localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, token.access_token)
  localStorage.setItem(ABP_USER_ID_STORAGE, sub)
  try {
    sessionStorage.removeItem(AUTH_STORAGE_PURGED_KEY)
  } catch {
    /* ignore */
  }
}
